import Collapse from "bootstrap/js/dist/collapse";
import ScrollSpy from "bootstrap/js/dist/scrollspy";
import Tooltip from "bootstrap/js/dist/tooltip";
import lozad from "lozad";

import "../styles/style.sass";

const loadCssLinks = document.querySelectorAll("[data-loadcss]");
loadCssLinks.forEach((link) => {
  if (navigator.userAgent.match("Edge/")) {
    link.media = "all";
  } else {
    link.addEventListener("load", function () {
      this.media = "all";
    });
  }
});

const trackScrollSpy = (event) => {
  const related = event?.relatedTarget;
  if (!related) return;
  // In Bootstrap 5, relatedTarget is an HTML element, not a string
  const href = typeof related === "string" ? related : related.getAttribute("href");
  if (!href) return;
  const currentPage = href.replace("#", "/");
  if (typeof window.umami !== "undefined" && currentPage) {
    window.umami.track(currentPage);
  }
};

// Initialize Bootstrap 5 ScrollSpy on documentElement (the actual scroll container)
const initScrollSpyTracking = () => {
  // Smallest section gap is 576px. Zone must be smaller to avoid overlapping sections.
  // Zone: 56px (navbar) to ~500px from top = ~50% of 1080px viewport
  // -56px top trims navbar area, -50% bottom trims lower half
  const scrollspy = ScrollSpy.getOrCreateInstance(document.documentElement, {
    target: "#navigation",
    rootMargin: "-56px 0px -50%",
    smoothScroll: true
  });
  
  // Listen for activate events on body (where they bubble to)
  document.body.addEventListener("activate.bs.scrollspy", trackScrollSpy);
};

const smoothScroll = (event) => {
  const link = event.currentTarget;
  if (!link || !link.hash) return;

  // Only handle mobile menu collapse - let Bootstrap's smoothScroll handle the actual scrolling
  const navbarCollapse = document.querySelector(".navbar-collapse");
  const toggler = document.querySelector(".navbar-toggler");
  if (
    navbarCollapse &&
    toggler &&
    getComputedStyle(toggler).display !== "none"
  ) {
    // Only hide if already shown - don't create instance if not needed
    const existingInstance = Collapse.getInstance(navbarCollapse);
    if (existingInstance) {
      existingInstance.hide();
    } else if (navbarCollapse.classList.contains('show')) {
      // Create instance only if we need to hide an open collapse
      Collapse.getOrCreateInstance(navbarCollapse).hide();
    }
  }
};

const initSmoothScroll = () => {
  const links = document.querySelectorAll(
    "a.js-scroll-trigger[href*='#']:not([href='#'])",
  );
  links.forEach((link) => link.addEventListener("click", smoothScroll));
};

const initNavbarVisibility = () => {
  const navbar = document.getElementById("navigation");
  const aboutSection = document.getElementById("about-uppersource");
  if (!navbar) return;
  
  // If no about section (e.g., other pages), show navbar immediately
  if (!aboutSection) {
    navbar.classList.add("navbar-visible");
    return;
  }

  const toggle = () => {
    // Show navbar when scrolled to or past the about section (minus navbar height)
    if (window.scrollY >= aboutSection.offsetTop - 56) {
      navbar.classList.add("navbar-visible", "navbar-shrink");
    } else {
      navbar.classList.remove("navbar-visible", "navbar-shrink");
    }
  };

  toggle();
  window.addEventListener("scroll", toggle, { passive: true });
};

const initTooltips = () => {
  const tooltipTriggerList = document.querySelectorAll(
    '[data-bs-toggle="tooltip"]',
  );
  tooltipTriggerList.forEach((trigger) => {
    Tooltip.getOrCreateInstance(trigger);
  });
};

const initValidation = () => {
  const forms = document.getElementsByClassName("needs-validation");
  Array.prototype.filter.call(forms, (form) => {
    form.addEventListener(
      "submit",
      (event) => {
        if (form.checkValidity() === false) {
          event.preventDefault();
          event.stopPropagation();
        }
        form.classList.add("was-validated");
      },
      false,
    );
  });
};

const initLazyLoad = () => {
  const observer = lozad(".lazyload", {
    rootMargin: "200px 0px",
  });
  observer.observe();
};

// Dynamically load Turnstile script when needed
const loadTurnstileScript = () => {
  return new Promise((resolve) => {
    if (typeof window.turnstile !== "undefined") {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => {
      // Wait for turnstile to be defined (may take a moment after script loads)
      const waitForTurnstile = () => {
        if (typeof window.turnstile !== "undefined") {
          resolve();
        } else {
          setTimeout(waitForTurnstile, 50);
        }
      };
      waitForTurnstile();
    };
    document.head.appendChild(script);
  });
};

const initContactFormAntiBot = () => {
  const form = document.getElementById("contactForm");
  if (!form) return;

  // Create form_started_at hidden input
  const startedAtInput = document.createElement("input");
  startedAtInput.type = "hidden";
  startedAtInput.name = "form_started_at";
  startedAtInput.value = Date.now().toString();
  form.appendChild(startedAtInput);

  // Read site key from meta tag
  const siteKeyMeta = document.querySelector('meta[name="cf-turnstile-site-key"]');
  if (!siteKeyMeta) return; // Local dev may not have it

  const siteKey = siteKeyMeta.getAttribute("content");
  if (!siteKey || siteKey === "YOUR_TURNSTILE_SITE_KEY") return;

  let turnstileLoaded = false;

  // Initialize Turnstile widget once script is loaded
  const initTurnstile = () => {
    const turnstileContainer = document.getElementById("contactTurnstile");
    if (!turnstileContainer) return;

    let widgetId = null;

    // Render Turnstile widget (deferred execution - runs on turnstile.execute())
    widgetId = window.turnstile.render(turnstileContainer, {
      sitekey: siteKey,
      size: "flexible",
      execution: "execute",
      appearance: "interaction-only",
      callback: (token) => {
        // Create or update hidden input with token
        let tokenInput = form.querySelector('input[name="cf-turnstile-response"]');
        if (!tokenInput) {
          tokenInput = document.createElement("input");
          tokenInput.type = "hidden";
          tokenInput.name = "cf-turnstile-response";
          form.appendChild(tokenInput);
        }
        tokenInput.value = token;

        // Submit form
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      },
      "error-callback": (errorCode) => {
        console.error("Turnstile error:", errorCode);
      },
    });

    form.dataset.turnstileWidgetId = widgetId;

    // Install submit handler (capture phase to run early)
    form.addEventListener("submit", (event) => {
      const existingToken = form.querySelector('input[name="cf-turnstile-response"]');
      if (existingToken && existingToken.value) {
        // Token present, allow normal submit
        return;
      }

      // Prevent default and execute Turnstile
      event.preventDefault();
      event.stopImmediatePropagation();

      // Validate form first
      if (form.checkValidity() === false) {
        form.classList.add("was-validated");
        return;
      }

      // Execute Turnstile challenge
      window.turnstile.execute(widgetId);
    }, true);
  };

  // Load Turnstile lazily when contact section is near viewport
  const loadTurnstileLazily = () => {
    if (turnstileLoaded) return;
    turnstileLoaded = true;

    loadTurnstileScript().then(initTurnstile);
  };

  // Use IntersectionObserver to defer Turnstile loading until contact section visible
  const contactSection = document.getElementById("contact");
  if (contactSection && "IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadTurnstileLazily();
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before visible
    );
    observer.observe(contactSection);
  } else {
    // Fallback: load immediately if no IntersectionObserver or no contact section
    loadTurnstileLazily();
  }
};

document.addEventListener("DOMContentLoaded", () => {
  initSmoothScroll();
  initScrollSpyTracking();
  initNavbarVisibility();
  initTooltips();
  initValidation();
  initLazyLoad();
  initContactFormAntiBot();
});
